// ============== CourseTestsModal.jsx (replace whole file) ==============
import { useEffect, useMemo, useState, useCallback } from "react"

import {
  X,
  ClipboardList,
  Trash2,
  LoaderCircle,
  Plus,
  Search,
  CheckSquare,
  Square,
  Filter,
  GripVertical,
  Link as LinkIcon,
  Clock,
  ListOrdered,
  CheckCircle,
} from "lucide-react"
import Select from "react-select"
import { AnimatePresence, motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import {
  getCourseTests,
  unmapCourseTests,
  mapCourseTests,
  getAvailableTests,
  reorderCourseTests,
  updateCourseTestWindow,
  getCourseTestPrereqs,
  saveCourseTestPrereqs,
} from "@/api/kuizy"
import { formatDateTime, formatDateTimeShort, typeExam_Order, typeOption_Order } from '@/settings/master'
import ExamDetailModal from '@/widgets/ExamDetailModal'
import { EXAM_STATUS } from "../settings/master.js"
// ===== datepicker =====
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

// ===== Helpers =====
const toJSDate = ts => (ts ? new Date(ts) : null)
const toISO = d => (d instanceof Date ? d.toISOString() : null)
const parsePgArray = v =>
  Array.isArray(v)
    ? v
    : typeof v === "string"
      ? v.replace(/[{}"]/g, "").split(",").map(s => s.trim()).filter(Boolean)
      : []
const toPgArray = arr => `{${(arr || []).join(",")}}`

// ===== DnD (dnd-kit) =====
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

function SortableRow({ id, children, className = "" }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <tr ref={setNodeRef} style={style} className={`${className} ${isDragging ? "opacity-60 ring-1 ring-primary" : ""}`}>
      {/* first cell is handle */}
      <td className="py-2 px-2 w-10 align-top">
        <button className="cursor-grab text-gray-500 hover:text-primary" {...attributes} {...listeners} title="ลากเพื่อจัดลำดับ">
          <GripVertical size={16} />
        </button>
      </td>
      {children}
    </tr>
  )
}

// ====== TimeCell editor (inline popover) with react-datepicker ======
function TimeCell({ courseTestId, begin, end, onSaved }) {
  const [open, setOpen] = useState(false)
  const [b, setB] = useState(toJSDate(begin))
  const [e, setE] = useState(toJSDate(end))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setB(toJSDate(begin))
    setE(toJSDate(end))
  }, [begin, end])

  const save = async () => {
    setSaving(true)
    try {
      await updateCourseTestWindow(courseTestId, toISO(b), toISO(e))
      setOpen(false)
      onSaved && onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative inline-flex items-center gap-2">
      <span className="text-xs text-gray-700">
        {!begin && !end
          ? 'ไม่จำกัดเวลา'
          : `${begin ? formatDateTimeShort(begin) : '-'} → ${end ? formatDateTimeShort(end) : '-'}`}
      </span>
      <button onClick={() => setOpen(v => !v)} className="text-primary underline text-xs" title="แก้ไขช่วงเวลา">
        แก้ไข
      </button>
      {open && (
        <div className="absolute z-50 top-full right-0 mt-2 w-[340px] bg-white border border-gray-200 rounded-lg shadow p-3">
          <div className="space-y-3">
            <div className="text-xs">เริ่ม</div>

            <DatePicker
              locale="th"
              selected={b}
              onChange={setB}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="d MMM yyyy HH:mm"
              placeholderText="เลือกวัน/เวลา"
              className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
            />



            <div className="text-xs">สิ้นสุด</div>
            <DatePicker
              locale="th"
              selected={e}
              onChange={setE}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="d MMM yyyy HH:mm"
              placeholderText="เลือกวัน/เวลา"
              minDate={b || undefined}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
            />




          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm border rounded">ยกเลิก</button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 text-sm bg-primary text-white rounded disabled:opacity-50">
              {saving ? <LoaderCircle size={14} className="animate-spin inline" /> : "บันทึก"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ====== Prerequisite Editor ======
function PrereqEditor({ courseId, targetCtId, candidates, initialRule, onClose, onSaved }) {
  const [selected, setSelected] = useState(parsePgArray(initialRule?.require_ct_ids))
  const [minReq, setMinReq] = useState(initialRule?.min_required || 1)
  const [saving, setSaving] = useState(false)





  useEffect(() => {
    setSelected(parsePgArray(initialRule?.require_ct_ids))
    setMinReq(initialRule?.min_required || 1)
  }, [initialRule])

  const allCount = Math.max(1, selected.length || 1)
  const modeAll = selected.length > 0 && minReq === selected.length

  const save = async () => {
    const clamped = Math.min(Math.max(1, minReq), selected.length || 1)
    setSaving(true)
    try {
      // ส่งเป็น Postgres array string แก้ปัญหาบันทึกไม่สำเร็จ
      await saveCourseTestPrereqs(courseId, targetCtId, selected, clamped)
      onSaved && onSaved()
    } finally {
      setSaving(false)
    }
  }

  const clearRule = async () => {
    setSaving(true)
    try {
      await saveCourseTestPrereqs(courseId, targetCtId, [], 0)
      onSaved && onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-[1200] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow p-4 w-full sm:w-[560px]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">กำหนดเงื่อนไขก่อนสอบ</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={18} /></button>
        </div>

        <label className="block text-sm mb-1">ต้องผ่านชุดต่อไปนี้</label>
        <div className="border rounded p-2 max-h-56 overflow-auto space-y-1">
          {candidates.map(c => {
            const checked = selected.includes(c.id)
            return (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    if (checked) setSelected(selected.filter(x => x !== c.id))
                    else setSelected([...selected, c.id])
                    if (minReq > (checked ? selected.length - 1 : selected.length + 1)) {
                      setMinReq(Math.max(1, (checked ? selected.length - 1 : selected.length + 1)))
                    }
                  }}
                />
                <span className="font-medium">{c.test_name}</span>
                <span className="text-xs text-gray-500">({c.content_name || c.content_code || "-"})</span>
              </label>
            )
          })}
          {candidates.length === 0 && <div className="text-xs text-gray-500">ไม่พบชุดข้อสอบอื่นในหลักสูตรนี้</div>}
        </div>

        <div className="mt-3 text-sm space-y-2">
          <label className="flex items-center gap-2">
            <input type="radio" checked={modeAll} onChange={() => setMinReq(Math.max(1, selected.length || 1))} />
            ต้องผ่าน “ทั้งหมด” ที่เลือก
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={!modeAll} onChange={() => setMinReq(Math.min(Math.max(1, minReq), allCount))} />
            ต้องผ่าน “อย่างน้อย”
            <input
              type="number"
              min={1}
              max={allCount}
              value={Math.min(minReq, allCount)}
              onChange={e => setMinReq(Math.min(Math.max(1, Number(e.target.value || 1)), allCount))}
              className="w-16 border rounded px-2 py-1"
            />
            จากที่เลือก
          </label>
        </div>

        <div className="flex justify-between gap-2 mt-4">
          <button onClick={clearRule} className="px-3 py-2 text-sm border rounded text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50" disabled={saving}>
            {saving && <LoaderCircle size={16} className="animate-spin inline mr-1" />} ลบเงื่อนไข
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 text-sm border rounded">ยกเลิก</button>
            <button onClick={save} disabled={selected.length === 0 || saving} className="px-3 py-2 text-sm bg-primary text-white rounded disabled:opacity-50">
              {saving && <LoaderCircle size={16} className="animate-spin inline mr-1" />} บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ====== Tests Picker (Add tests to course) ======
function TestsPickerModal({ courseId, onSuccess, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("active")
  const [selected, setSelected] = useState({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    getAvailableTests({ course_id: courseId, status, search: search || undefined })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : r.data?.data ?? []
        setRows(data)
        setSelected({})
      })
      .finally(() => setLoading(false))
  }, [courseId, status, search])
  useEffect(load, [load])

  const idsOnPage = useMemo(() => rows.map(t => t.id), [rows])
  const allSelected = rows.length > 0 && idsOnPage.every(id => selected[id])
  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected])
  const selectedCount = selectedIds.length

  const toggleOne = id => setSelected(s => (s[id] ? { ...s, [id]: undefined } : { ...s, [id]: true }))
  const toggleAll = () =>
    setSelected(s => {
      if (allSelected) {
        const n = { ...s }
        idsOnPage.forEach(id => delete n[id])
        return n
      } else {
        const n = { ...s }
        idsOnPage.forEach(id => (n[id] = true))
        return n
      }
    })

  const save = async () => {
    if (selectedIds.length === 0) return
    setSaving(true)
    try {
      await mapCourseTests(courseId, selectedIds)
      onSuccess && onSuccess()
    } finally {
      setSaving(false)
    }
  }

  const statusOptions = [
    { value: "", label: "ทั้งหมด" },
    { value: "active", label: "ใช้งาน" },
    { value: "inactive", label: "ปิด" },
  ]
  const statusValue = statusOptions.find(o => o.value === status)

  return (
    <motion.div key="tpBack" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
      <motion.div key="tpCard" initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 28, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
          <h2 className="font-semibold text-sm sm:text-base">เลือกชุดข้อสอบ</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
        </header>

        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-info border-b border-gray-200">
          <div className="flex flex-col gap-1 w-44">
            <label className="flex items-center gap-1 text-sm"><Filter size={16} /> สถานะ</label>
            <Select className="rs-select" classNamePrefix="rs" options={statusOptions} value={statusValue} onChange={o => setStatus(o?.value ?? "")} isSearchable={false} />
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <label className="flex items-center gap-1 text-sm mb-1"><Search size={16} /> ค้นหา</label>
            <Search size={16} className="absolute left-3 top-[42px] -translate-y-1/2 text-gray-500 pointer-events-none" />
            <div className="flex items-center gap-2">
              <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && setSearch(searchIn)} placeholder="ค้นหาชื่อชุดข้อสอบ" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
              <button onClick={() => setSearch(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"><Search size={16} /> ค้นหา</button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-3 w-12 text-left">
                  <button onClick={toggleAll} className="text-primary hover:text-primary/80" disabled={rows.length === 0}>
                    {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </th>
                <th className="py-2 px-3 text-left">ชื่อชุดข้อสอบ</th>
                <th className="py-2 px-3 text-left">เนื้อหา</th>
                <th className="py-2 px-3 text-left hidden md:table-cell">ช่วงใช้งานชุดสอบ</th>
                <th className="py-2 px-3 text-left hidden sm:table-cell">ระยะเวลา</th>
                <th className="py-2 px-3 text-left hidden lg:table-cell">เกณฑ์ผ่าน</th>
                <th className="py-2 px-3 text-left hidden lg:table-cell">จำนวนข้อสอบ</th>
                <th className="py-2 px-3 text-left hidden lg:table-cell">เรียงข้อสอบ</th>
                <th className="py-2 px-3 text-left hidden lg:table-cell">เรียงตัวเลือก</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8"><LoaderCircle className="animate-spin mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-500">– ไม่มีข้อมูล –</td></tr>
              ) : (
                rows.map(t => {
                  const id = t.id
                  const checked = !!selected[id]
                  return (
                    <tr key={id} onClick={() => toggleOne(id)} className={`cursor-pointer ${checked ? "bg-primary/10" : "odd:bg-white even:bg-gray-50"} whitespace-nowrap`}>
                      <td className="py-2 px-3 text-center">{checked ? <CheckSquare size={16} /> : <Square size={16} />}</td>
                      <td className="py-1.5 px-3" >   {t.name || t.test_name}</td>
                      <td className="py-1.5 px-3">{t.content_name || t.content_name || "-"}</td>
                      <td className="py-1.5 px-3 hidden md:table-cell">{t.start_time || t.end_time ? `${t.start_time ? formatDateTime(t.start_time) : "-"} → ${t.end_time ? formatDateTime(t.end_time) : "-"}` : "-"}</td>
                      <td className="py-1.5 px-3 hidden sm:table-cell">{t.duration ? `${t.duration} นาที` : "-"}</td>
                      <td className="py-1.5 px-3 hidden lg:table-cell">{t.criteria ? `${t.criteria}%` : "-"}</td>
                      <td className="py-1.5 px-3 hidden sm:table-cell">{t.question_count ? `${t.question_count} ข้อ` : "-"}</td>
                      <td className="py-1.5 px-3 hidden sm:table-cell">   {typeExam_Order[t.exam_order] || 'normal'}   </td>
                      <td className="py-1.5 px-3 hidden sm:table-cell">{typeOption_Order[t.option_order] || 'normal'}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">เลือกแล้ว {selectedCount} รายการ</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
            <button onClick={save} disabled={selectedCount === 0 || saving} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
              {saving && <LoaderCircle size={16} className="animate-spin" />} <Plus size={16} /> เพิ่มชุดข้อสอบ
            </button>
          </div>
        </div>
      </motion.div>

    </motion.div>
  )
}

// ===== Main Modal =====
export default function CourseTestsModal({ courseId, courseName, onClose }) {
  const [rows, setRows] = useState([]) // course_tests joined tests
  const [loading, setLoading] = useState(true)
  const [picker, setPicker] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")

  // prerequisites cache keyed by target_ct_id
  const [prereqMap, setPrereqMap] = useState({})
  const [prereqEditing, setPrereqEditing] = useState(null) // ct.row for editor

  const [detailOpen, setDetail] = useState(false)
  const [selTestId, setSelTest] = useState(null)
  const [selcId, setSelCid] = useState(null)

  const openDetail = t => { if (!t.test_id) return; setSelTest(t.test_id); setSelCid(t.content_id); setDetail(true) }

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([getCourseTests(courseId), getCourseTestPrereqs(courseId)])
      .then(([r1, r2]) => {
        const data = Array.isArray(r1.data) ? r1.data : r1.data?.data ?? []
        setRows(data)
        const rules = Array.isArray(r2.data) ? r2.data : r2.data?.data ?? []
        const map = {}
        rules.forEach(rule => {
          map[rule.target_ct_id] = { ...rule, require_ct_ids: parsePgArray(rule.require_ct_ids) }
        })
        setPrereqMap(map)
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [courseId])
  useEffect(load, [load])

  // search
  const list = useMemo(() => {
    const t = search.trim().toLowerCase()
    if (!t) return rows
    return rows.filter(
      x =>
        (x.test_name || "").toLowerCase().includes(t) ||
        (x.content_code || "").toLowerCase().includes(t) ||
        (x.content_name || "").toLowerCase().includes(t)
    )
  }, [rows, search])

  // DnD ordering by test_id (as backend expects)
  const ids = useMemo(() => list.map(x => x.test_id), [list])
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const onDragEnd = async evt => {
    const { active, over } = evt
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(active.id)
    const newIndex = ids.indexOf(over.id)
    const newOrder = arrayMove(ids, oldIndex, newIndex)
    try {
      await reorderCourseTests(courseId, newOrder)
    } catch { }
    load()
  }

  const confirmUnmapOne = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      await unmapCourseTests(courseId, [delItem.test_id])
      setDelItem(null)
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถยกเลิกได้" })
    } finally {
      setDeleting(false)
    }
  }

  const prereqSummary = ctId => {
    const r = prereqMap[ctId]
    const ids = parsePgArray(r?.require_ct_ids)
    if (!r || ids.length === 0) return "– ไม่มี –"
    const m = ids.length
    // if ((r.min_required || 1) >= m) return `ต้องผ่านทั้งหมด (${m})`
    return `ต้องผ่าน ${r.min_required}/${m}`
  }

  return (
    <AnimatePresence>
      <motion.div
        key="ctBack"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4"
      >
        <motion.div
          key="ctCard"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full h-full sm:h-[95vh] sm:w-[95%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
            <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
              <ClipboardList size={18} /> ชุดข้อสอบของ “{courseName}” ({list.length})
            </h2>
            <button onClick={onClose} className="text-gray-600 hover:text-primary">
              <X size={20} />
            </button>
          </header>

          <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
            <button onClick={() => setPicker(true)} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1">
              <Plus size={14} /> เพิ่มชุดข้อสอบ
            </button>
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  value={searchIn}
                  onChange={e => setSearchIn(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
                  placeholder="ค้นหาชื่อชุดข้อสอบ / ชื่อย่อชุดข้อสอบ"
                  className="w-[260px] border border-gray-300 rounded-lg pl-9 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary"
                />
              </div>
              <button onClick={() => setSearch(searchIn)} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-1.5 text-sm">
                <Search size={16} /> ค้นหา
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <table className="min-w-[1080px] w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-2 w-10 text-left"></th>
                      <th className="py-2 px-3 text-left">ชื่อชุดข้อสอบ</th>
                      <th className="py-2 px-3 text-left">เนื้อหา</th>
                      <th className="py-2 px-3 text-left">สถานะ</th>
                      <th className="py-2 px-3 text-left hidden xl:table-cell">รายละเอียด</th>
                      <th className="py-2 px-3 text-left hidden md:table-cell">ช่วงเวลาที่อนุญาต (ในหลักสูตร)</th>
                      <th className="py-2 px-3 text-left hidden sm:table-cell">เงื่อนไขก่อนสอบ</th>
                      <th className="py-2 px-3 w-16 text-right">ลบ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8">
                          <LoaderCircle className="animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : list.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-gray-500">
                          – ไม่มีข้อมูล –
                        </td>
                      </tr>
                    ) : (
                      list.map(r => (
                        <SortableRow key={r.test_id} id={r.test_id} className="odd:bg-white even:bg-gray-50 align-top">
                          <td className="py-1.5 px-3">
                            <div className="font-medium text-primary hover:underline cursor-default" onClick={() => openDetail(r)}  >{r.test_name}</div>
                          </td>
                          <td className="py-1.5 px-3">{r.content_name || r.content_code || "-"}</td>
                          <td className="py-1.5 px-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${EXAM_STATUS[r.approval_status].style}`}>
                              {EXAM_STATUS[r.approval_status].label}
                            </span>
                          </td>
                          <td className="py-1.5 px-3 hidden xl:table-cell">
                            <div className="flex flex-wrap gap-3 text-xs text-gray-700">
                              <span className="inline-flex items-center gap-1">
                                <CheckCircle size={14} /> {r.criteria ? `${r.criteria}%` : "-"}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <Clock size={14} /> {r.duration ? `${r.duration} นาที` : "-"}
                              </span>
                              <span className="inline-flex items-center gap-1">ข้อ: {r.question_count ?? "-"}</span>

                              <span className="inline-flex items-center gap-1">
                                <ListOrdered size={14} />คำถาม{typeExam_Order[r.exam_order] || 'normal'} ,คำตอบ{typeOption_Order[r.option_order] || 'normal'}
                              </span>


                            </div>
                          </td>
                          <td className="py-1.5 px-3 hidden md:table-cell">
                            <TimeCell courseTestId={r.id} begin={r.begin} end={r.end} onSaved={load} />
                          </td>
                          <td className="py-1.5 px-3 hidden sm:table-cell">
                            <div className="flex items-center gap-2 text-xs">
                              <LinkIcon size={14} className="text-secondary" />
                              <span>{prereqSummary(r.id)}</span>
                              <button onClick={() => setPrereqEditing(r)} className="text-primary underline">
                                แก้ไข
                              </button>
                            </div>
                          </td>
                          <td className="py-1.5 px-3 text-right">
                            <button onClick={() => setDelItem(r)} className="text-red-600 hover:text-red-800" title="นำออก">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </SortableRow>
                      ))
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>

          <AnimatePresence>
            {picker && (
              <TestsPickerModal
                courseId={courseId}
                onSuccess={() => {
                  setPicker(false)
                  load()
                }}
                onClose={() => setPicker(false)}
              />
            )}
          </AnimatePresence>

          {prereqEditing && (
            <PrereqEditor
              courseId={courseId}
              targetCtId={prereqEditing.id}
              candidates={rows.filter(x => x.id !== prereqEditing.id)}
              initialRule={prereqMap[prereqEditing.id]}
              onClose={() => setPrereqEditing(null)}
              onSaved={() => {
                setPrereqEditing(null)
                load()
              }}
            />
          )}

          {delItem && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบ"
              message={`ลบ “${delItem.test_name}” ออกจากหลักสูตรนี้ ?`}
              pending={deleting}
              onConfirm={confirmUnmapOne}
              onCancel={() => setDelItem(null)}
            />
          )}
          {alert.open && (
            <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />
          )}

          {detailOpen &&
            <ExamDetailModal visible={detailOpen} testId={selTestId} cid={selcId} ctId={courseId} onClose={() => { setDetail(false); load(); }} />}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
